import { isTableSeparator } from '@sonasi/shared-types';
/**
 * The Markdown the two editors write, and the preview beside it.
 *
 * Both editors in `Sonasi Admin v2.dc.html` have the same eight-button toolbar
 * and the same Write / Preview pair, so the rules live here once rather than
 * twice.
 */

export interface MarkdownTool {
  name: string;
  /** What the button shows: the design's own two-character marks. */
  icon: string;
  before: string;
  after: string;
  /**
   * Whether the mark belongs to the whole line.
   *
   * `## ` is a property of a line: wrapping half a sentence in it produces a
   * heading that starts mid-paragraph. `**` is a property of a selection.
   */
  line?: boolean;
}

export const MARKDOWN_TOOLS: MarkdownTool[] = [
  { name: 'Heading', icon: 'H2', before: '## ', after: '', line: true },
  { name: 'Bold', icon: 'B', before: '**', after: '**' },
  { name: 'Italic', icon: 'I', before: '*', after: '*' },
  { name: 'Inline code', icon: '`', before: '`', after: '`' },
  { name: 'Bulleted list', icon: '•', before: '- ', after: '', line: true },
  { name: 'Numbered list', icon: '1.', before: '1. ', after: '', line: true },
  { name: 'Link', icon: '[]', before: '[', after: '](https://)' },
  { name: 'Quote', icon: '>', before: '> ', after: '', line: true },
];

/** Any line marker, so choosing a second one replaces the first. */
const LINE_MARK = /^(#{1,6} |> |- |\d+\. )/;

export interface Edit {
  text: string;
  /** Where the cursor should be afterwards. */
  start: number;
  end: number;
}

/**
 * Applies one tool to the text, at the current selection.
 *
 * A line tool rewrites the line the cursor sits in and swaps whatever marker
 * was already there — picking Quote after Heading gives `> `, not `> ## `. A
 * wrapping tool brackets the selection, and with nothing selected it drops the
 * pair in and puts the cursor between them, which is where somebody is about
 * to type.
 */
export const applyTool = (
  text: string,
  selectionStart: number,
  selectionEnd: number,
  tool: MarkdownTool,
): Edit => {
  if (tool.line) {
    const lineStart = text.lastIndexOf('\n', Math.max(selectionStart - 1, 0)) + 1;
    const lineEnd = text.indexOf('\n', selectionStart);
    const end = lineEnd === -1 ? text.length : lineEnd;
    const line = text.slice(lineStart, end);
    const bare = line.replace(LINE_MARK, '');
    // Pressing the same tool again takes the marker off, so the button is a
    // toggle rather than a way to stack `> > > `.
    const already = line.startsWith(tool.before);
    const marked = already ? bare : `${tool.before}${bare}`;
    const moved = marked.length - line.length;

    return {
      text: `${text.slice(0, lineStart)}${marked}${text.slice(end)}`,
      start: Math.max(lineStart, selectionStart + moved),
      end: Math.max(lineStart, selectionEnd + moved),
    };
  }

  const selected = text.slice(selectionStart, selectionEnd);
  const wrapped = `${tool.before}${selected}${tool.after}`;

  return {
    text: `${text.slice(0, selectionStart)}${wrapped}${text.slice(selectionEnd)}`,
    start: selectionStart + tool.before.length,
    end: selectionStart + tool.before.length + selected.length,
  };
};

/**
 * How many words the text has, with the Markdown taken off.
 *
 * `##`, `>` and a fence are not words, and counting them makes the number climb
 * every time somebody adds a heading — the opposite of what the writer is
 * watching it for.
 */
export const wordCount = (text: string): number =>
  text
    .split('\n')
    .filter((line) => !/^\s*```/.test(line))
    // A table's separator row is drawn, not read; its pipes are not words.
    .filter((line) => !isTableSeparator(line))
    .map((line) => line.replace(/^\s*(#{1,6}|>|[-*+]|\d+\.)\s+/, ''))
    .map((line) => line.replace(/\|/g, ' '))
    .join(' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

/*
 * The parser moved to `@sonasi/shared-types` when the website started
 * rendering published posts with it: the editor's preview and the reader's
 * page have to agree, and one copy is how they stay agreeing. Re-exported so
 * `MarkdownField` keeps importing everything it needs from one place.
 */
export {
  isTableSeparator,
  parseInline,
  parseMarkdown,
  type Block,
  type Inline,
} from '@sonasi/shared-types';
